import React from 'react';
import { Flex, Select, SelectProps } from 'antd';
import { DefaultOptionType } from 'antd/es/select';
import { escapeRegExp } from 'lodash-es';
import { css } from 'styled-components';

import { useDevExTheme } from '../common/themes/DevExTheme';

import { DevExText } from './DevExText';

const styles = {
  select: css`
    &&&&.ant-select {
      min-width: 140px;

      .ant-select-selector {
        background: ${(props) => props.theme.color.background.raised};
        transition: none;

        .ant-select-selection-item,
        .ant-select-selection-search input {
          font-size: ${(props) => props.theme.variable.fontSize.sm};
          color: ${(props) => props.theme.color.text.primary};
        }

        .select__placeholder {
          color: ${(props) => props.theme.color.text.placeholder};
          font-weight: 400;
          display: flex !important; // unset display: none
        }
      }

      .ant-select-arrow,
      .ant-select-clear {
        color: ${(props) => props.theme.color.text.tertiary};
      }

      &.ant-select-lg {
        .ant-select-selector {
          border-radius: ${(props) => props.theme.variable.borderRadius};

          .ant-select-selection-item,
          .ant-select-selection-search input {
            font-size: ${(props) => props.theme.variable.fontSize.md};
          }
        }
      }
    }
  `,
};

export type DevExSelectOption = DefaultOptionType;

export interface DevExSelectProps extends SelectProps {}

export const DevExSelect: React.FC<DevExSelectProps> = ({ ...props }) => {
  const theme = useDevExTheme();

  const placeholder = props.placeholder && (
    <Flex
      className="select__placeholder"
      gap={theme.variable.spacing.xs}
      align="center"
      style={{ display: 'none' }} // don't display placeholder by default to avoid it appearing in dropdown
    >
      {props.placeholder}
      {Array.isArray(props.value) ? !!props.value.length : !!props.value ? ':' : null}
    </Flex>
  );

  let options = [...(props.options ?? [])];

  const filterOption = (searchQuery: string, option: DevExSelectOption) => {
    const regex = new RegExp(escapeRegExp(searchQuery), 'i');
    return !!option.label && regex.test(option.label.toString());
  };

  return (
    <div style={{ position: 'relative' }}>
      <Select
        css={styles.select}
        filterOption={filterOption}
        notFoundContent={
          <DevExText
            style={{ textAlign: 'center', width: '100%', padding: theme.variable.spacing.sm }}
            type="secondary"
          >
            No items found
          </DevExText>
        }
        {...props}
        prefix={
          props.prefix ? (
            <Flex
              align="center"
              style={{ color: theme.color.text.tertiary }}
              gap={theme.variable.spacing.sm}
            >
              {props.prefix}:
            </Flex>
          ) : undefined
        }
        value={props.value}
        onChange={(value, option) => {
          props.onChange?.(value, option);
        }}
        options={options}
        placeholder={placeholder}
        popupMatchSelectWidth={false}
      />
    </div>
  );
};
